<template>
  <loader :is-loading="isLoading" color="primary">
    <v-flex>
      <notifications group="user-form-error" position="top center" />
      <v-card flat id="user-card" class="pa-3">
        <v-card-title id="employee-title" class="form-title">
          <span>{{ $t(`components.usersForm.add.title`) }}</span>
          <v-spacer></v-spacer>
          <v-icon @click="forceClear">mdi-close</v-icon>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12">
              <span class="text-uppercase input-label">
                {{ $t(`components.usersForm.default.name`) }}
              </span>
            </v-col>

            <v-col cols="6">
              <v-text-field
                :placeholder="$t(`components.usersForm.default.givenName`)"
                :class="{ 'employee-field-error': $v.user.givenName.$error }"
                :value="user.givenName"
                class="employee-field"
                height="44"
                dense
                outlined
                clearable
                hide-details
                @input="formState.patchField('givenName', $event)"
                @blur="$v.user.givenName.$touch()"
              >
                <template v-if="$v.user.givenName.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.user.givenName" />
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="6">
              <v-text-field
                :value="user.familyName"
                :class="{ 'employee-field-error': $v.user.familyName.$error }"
                :placeholder="$t(`components.usersForm.default.familyName`)"
                class="employee-field"
                height="44"
                outlined
                clearable
                hide-details
                no-filter
                dense
                @input="formState.patchField('familyName', $event)"
                @blur="$v.user.familyName.$touch()"
              >
                <template v-if="$v.user.familyName.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.user.familyName" />
                </template>
              </v-text-field>
            </v-col>

            <v-col>
              <span class="text-uppercase input-label">{{
                $t(`components.usersForm.default.email`)
              }}</span>
            </v-col>

            <v-col cols="12">
              <v-text-field
                :value="user.email"
                :placeholder="
                  $t(`components.usersForm.default.emailPlaceholder`)
                "
                :class="{ 'employee-field-error': $v.user.email.$error }"
                class="employee-field email-input"
                height="44"
                outlined
                clearable
                hide-details
                no-filter
                dense
                @input="formState.patchField('email', $event)"
                @blur="$v.user.email.$touch()"
              >
                <template v-if="$v.user.email.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.user.email" />
                </template>
              </v-text-field>
            </v-col>

            <v-col>
              <span class="text-uppercase input-label">{{
                $t(`components.usersForm.default.commLang`)
              }}</span>
            </v-col>

            <v-col cols="12">
              <v-select
                :items="languageList"
                :value="user.communicationLanguage"
                class="employee-field"
                :menu-props="{ bottom: true, offsetY: true }"
                item-text="name"
                item-value="value"
                outlined
                hide-details
                dense
                height="44"
                background-color="#f8f8f8"
                :class="{
                  'input-field-error': $v.user.communicationLanguage.$error,
                }"
                @change="formState.patchField('communicationLanguage', $event)"
              >
                <template
                  v-if="$v.user.communicationLanguage.$anyError"
                  v-slot:append
                >
                  <error-tooltip :validator="$v.user.communicationLanguage" />
                </template>
              </v-select>
            </v-col>

            <v-col>
              <span class="text-uppercase input-label">{{
                $t(`components.usersForm.default.role`)
              }}</span>
            </v-col>

            <v-col cols="12">
              <v-select
                :items="roles"
                :value="user.role"
                class="employee-field"
                item-text="name"
                item-value="value"
                :menu-props="{ bottom: true, offsetY: true }"
                :class="{ 'input-field-error': $v.user.role.$error }"
                outlined
                hide-details
                dense
                height="44"
                @change="formState.patchField('role', $event)"
              >
                <template v-if="$v.user.role.$anyError" v-slot:append>
                  <error-tooltip :validator="$v.user.role" />
                </template>
              </v-select>
            </v-col>

            <v-col v-if="isCorporationAdmin">
              <span class="text-uppercase input-label">{{
                $t(`components.usersForm.default.corporations`)
              }}</span>
            </v-col>

            <v-col cols="12" v-if="isCorporationAdmin">
              <v-autocomplete
                v-model="selectedCorporation"
                outlined
                filled
                icon="$icons_product_corporation"
                item-text="name"
                item-value="id"
                height="44"
                dense
                class="employee-field-autocomplete"
                :placeholder="$t('components.usersForm.default.corporations')"
                :items="allCorporations"
                hide-details
                return-object
                @change="onCorporationChange"
              />

              <notifications group="corporations-error" position="top center" />
            </v-col>

            <v-col
              v-if="user.roles.length"
              cols="12"
              class="corporation-list-wrapper"
            >
              <div
                v-for="role in user.roles"
                :key="role.corporationId"
                class="d-flex align-center justify-space-between corporation-item"
              >
                <div>
                  <p class="mb-1 text-body-1">
                    {{ getActiveCorporationById(role.corporationId).name }}
                  </p>
                  <span class="text-subtitle-2">{{
                    getActiveCorporationById(role.corporationId).code
                  }}</span>
                  <span class="text-subtitle-2">
                    -
                    {{
                      getActiveCorporationById(role.corporationId).contactEmail
                    }}</span
                  >
                </div>

                <v-btn
                  @click="removeCorporation(role.corporationId)"
                  class="corporation-remove-btn"
                  icon
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </v-col>

            <v-col cols="12" v-if="isCorporationAdmin" class="py-0">
              <v-checkbox
                v-model="user.createPassengerAccount"
                :label="$t(`components.usersForm.default.corporAdminCheckbox`)"
                hide-details
              ></v-checkbox>
            </v-col>

            <v-col v-if="user.createPassengerAccount">
              <span class="text-uppercase input-label">{{
                $t(`components.usersForm.default.phone`)
              }}</span>
            </v-col>

            <v-col v-if="user.createPassengerAccount" cols="12">
              <vue-tel-input
                ref="user-phone-input"
                filled
                mode="national"
                :class="{ 'employee-field-error': $v.phoneNumber.$error }"
                :autoDefaultCountry="false"
                :defaultCountry="countryCode"
                :dropdownOptions="{
                  showDialCodeInSelection: true,
                  showFlags: true,
                  showSearchBox: true,
                }"
                :inputOptions="{ showDialCode: false }"
                v-model.trim="phoneNumber"
                @input="onPhoneNumberChange"
              >
                <template
                  v-if="$v.phoneNumber.$error && !$v.phoneNumber.required"
                  v-slot:icon-right
                >
                  <div class="mr-2 mt-2">
                    <error-tooltip :validator="$v.phoneNumber" />
                  </div>
                </template>
              </vue-tel-input>
            </v-col>
            <v-col v-if="addPassengerErrors" cols="12">
              <v-alert dense outlined type="error" class="mb-0">
                {{ addPassengerErrors.messageTitle }}
              </v-alert>
              <v-list dense>
                <v-list-item
                  three-line
                  v-for="(error, index) in addPassengerErrors.details"
                  :key="index"
                  class="pa-0"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{
                      error.businessAccountName
                    }}</v-list-item-title>
                    <v-list-item-subtitle>{{
                      error.translatedErrorMessage
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col v-if="user.createPassengerAccount && user.givenName">
              <p class="mb-0">
                <span class="font-weight-bold text--black">{{
                  `${user.givenName}`
                }}</span>
                {{
                  $t(`components.usersForm.default.confirmation`, {
                    attribute: companyName,
                  })
                }}
              </p>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="mt-4">
          <v-btn
            class="text-capitalize"
            color="grey darken-1"
            text
            @click="forceClear"
          >
            {{ $t(`components.usersForm.add.cancel`) }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            class="text-capitalize white--text"
            color="#333333"
            depressed
            :disabled="isFormInvalid"
            @click="submitForm"
          >
            {{ $t(`components.usersForm.add.agree`) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </loader>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import userValidationData from "@/helpers/rules/userValidationData";
import ErrorTooltip from "@/components/elements/ErrorTooltip.vue";

import { CHANNEL_ADMIN, CORP_ADMIN } from "@/helpers/userRoles";
import { languageMethods, languageList } from "@/mixins/language.mixins";

import { userState, transformerForUserRoles } from "./user.mixin";

export default {
  name: "CreateUserForm",
  components: {
    ErrorTooltip,
  },
  mixins: [languageMethods, languageList],
  props: {
    allCorporations: {
      type: Array,
      required: true,
    },
  },
  created() {
    this.formState = userState({
      givenName: "",
      familyName: "",
      email: "",
      communicationLanguage: "en",
      role: "corporationAdmin",
      roles: [],
      createPassengerAccount: false,
      cachedPhoneNumber: null,
    });
  },
  data() {
    return {
      formState: null,
      isLoading: false,
      corporations: [],
      corporationsLoading: false,
      selectedCorporation: null,
      addPassengerErrors: null,
      phone: null,
      phoneNumber: null,
      roles: [
        {
          name: this.$t("components.usersForm.default.corpoAdmin"),
          value: CORP_ADMIN,
        },
        {
          name: this.$t("components.usersForm.default.channelAdmin"),
          value: CHANNEL_ADMIN,
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      countryCode: "bookingChannel/defaultCountry",
    }),
    countryCode: {
      get() {
        return this.$store.getters["bookingChannel/defaultCountry"];
      },
      set(newCountryCode) {
        this.setCountryCode(newCountryCode);
      },
    },
    isCorporationAdmin() {
      if (!this.formState) {
        return false;
      }

      return this.formState.state.role === CORP_ADMIN;
    },
    user() {
      if (!this.formState) {
        return {};
      }

      return this.formState.state;
    },
    isFormInvalid() {
      return this.$v.user.$invalid;
    },
  },
  methods: {
    ...mapMutations("bookingChannel", ["setCountryCode"]),
    forceClear() {
      this.formState.resetState();
      this.$emit("closeDialog");
    },
    getActiveCorporationById(id) {
      const corporation = this.allCorporations.find((item) => item.id === id);

      if (!corporation) {
        return {};
      }

      return corporation;
    },
    async createPassengerAccount() {
      const result = await this.$store.dispatch(
        "businessPassenger/createBusinessPassenger",
        {
          givenName: this.user.givenName,
          familyName: this.user.familyName,
          email: this.user.email,
          phone: this.phone,
          corporationIds: this.user.roles.map((role) => role.corporationId),
          returnErrorDetails: true,
        }
      );

      if (result.details) {
        this.addPassengerErrors = result.details;
        this.isLoading = false;

        this.$notify({
          group: "user-form-error",
          type: "error",
          title: "Error",
          text: this.$t(
            "components.bookingForm.validation.passenger-create-error"
          ),
        });

        return { isError: true };
      }

      return { isError: false };
    },
    async submitForm() {
      // If form is invalid, notify the user and return early
      if (this.isFormInvalid) {
        this.$notify({
          group: "user-form-error",
          type: "error",
          title: "Error",
          text: this.$t("components.usersForm.validation.error"),
        });
        return;
      }

      try {
        // Set loading state to true
        this.isLoading = true;

        // Check if a passenger account should be created
        if (this.user.createPassengerAccount) {
          const { isError } = await this.createPassengerAccount();

          // If error occurred during passenger account creation, stop further execution
          if (isError) {
            this.$notify({
              group: "user-form-error",
              type: "error",
              title: "Error",
              text: this.$t(
                "components.usersForm.validation.passenger-account-error"
              ),
            });
            return;
          }
        }

        // Dispatch the store action to create a new user
        const url = "users/createUser";
        const createdUser = await this.$store.dispatch(url, {
          ...this.user,
          roles: transformerForUserRoles(this.user.role, this.user.roles),
        });

        // If user creation is successful, notify the user
        if (createdUser) {
          this.$notify({
            group: "user-form-success",
            type: "success",
            title: "Success",
            text: this.$t(
              "components.bookingForm.validation.user-create-success"
            ),
          });

          // Optionally clear the form or reset it
          this.forceClear();
        }
      } catch (error) {
        // If any error occurs during the process, notify the user
        this.$notify({
          group: "user-form-error",
          type: "error",
          title: "Error",
          text: this.$t("components.bookingForm.validation.user-create-error"),
        });
      } finally {
        // Set loading state to false regardless of success or failure
        this.isLoading = false;
      }
    },
    onCorporationChange(val) {
      if (this.formState.isRoleAdded(val.id)) {
        this.$notify({
          group: "corporations-error",
          type: "error",
          title: "Error",
          text: this.$t(
            "components.bookingForm.validation.corporationAlreadyAdded"
          ),
        });
        return;
      }

      if (val) {
        this.formState.patchField("roles", {
          name: val.name,
          corporationId: val.id,
        });
      }

      this.selectedCorporation = null;
    },
    removeCorporation(id) {
      this.formState.removeRole(id);
    },
    onPhoneNumberChange(number, phoneObject) {
      if (phoneObject.valid) {
        this.phoneNumber = number;
        this.phone = phoneObject.number;
      }
    },
  },
  validations: {
    ...userValidationData,
  },
};
</script>

<style lang="scss" scoped>
.opacity-loading {
  opacity: 0.5;
}
#user-card {
  .input-label {
    font-style: normal;
    font-weight: 400;
    font-size: 10px;
    line-height: 12px;
    display: flex;
    align-items: center;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #888888;
  }

  .employee-field {
    background: #f8f8f8;
    box-sizing: border-box;
    border-radius: 6px;
    font-style: normal;
    line-height: 15px;
    border: 1px solid #eeeeee;
    letter-spacing: -0.005em;
    color: #333333;

    & fieldset {
      box-shadow: none !important;
    }
  }

  .employee-field:focus-within {
    border-color: #47d061;
  }

  .employee-field-error {
    border: 1px solid #eb5757 !important;
  }

  .corporation-list-wrapper {
    max-height: 250px;
    overflow-y: auto;
  }

  .corporation-item {
    background: #f8f8f8;
    border-radius: 6px;
    padding: 10px;
    margin-bottom: 10px;
    border-bottom: 1px solid #eeeeee;

    & p {
      color: #333333;
    }
  }
}
</style>
